import { AlertCircle, ExternalLink, FileDown, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { PDFDocument } from "pdf-lib";

export default function PdfViewer({ url }: { url: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // State baru untuk mengecek apakah file bisa di-render browser
  const [isRenderable, setIsRenderable] = useState(true);

  // Helper untuk mendeteksi ekstensi yang tidak bisa dibuka iframe
  const checkIsRenderable = (fileUrl: string, contentType: string) => {
    const lowerUrl = fileUrl.toLowerCase();

    // Ekstensi file Office/Arsip yang pasti gagal di iframe
    const unrenderableExtensions = [
      ".ppt",
      ".pptx",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".zip",
      ".rar",
      ".7z",
    ];
    if (unrenderableExtensions.some((ext) => lowerUrl.includes(ext))) {
      return false;
    }

    // Tipe MIME yang aman untuk dirender
    const safeTypes = ["application/pdf", "image/", "text/plain"];
    if (safeTypes.some((type) => contentType.toLowerCase().includes(type))) {
      return true;
    }

    // Jika server merespon dengan octet-stream tapi URL-nya .pdf/.jpg, kita anggap aman
    if (
      lowerUrl.includes(".pdf") ||
      lowerUrl.includes(".jpg") ||
      lowerUrl.includes(".png")
    ) {
      return true;
    }

    return false; // Default: anggap tidak bisa dirender agar aman
  };

  useEffect(() => {
    if (!url) return;

    let isMounted = true;
    setIsLoading(true);
    setError(false);
    setIsRenderable(true);

    const loadAndProcessPdf = async () => {
      try {
        // ==========================================
        // 1. LOGIKA PENYESUAIAN URL PROXY
        // ==========================================
        let fetchUrl = url;
        if (url.includes("static1.undiksha.ac.id")) {
          const urlObj = new URL(url);
          fetchUrl = `/static${urlObj.pathname}${urlObj.search}`;
        }

        // ==========================================
        // 2. FETCH FILE (ARRAY BUFFER)
        // ==========================================
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error("Gagal mengambil dokumen");

        const contentType = response.headers.get("content-type") || "";

        // Pengecekan keamanan apakah file ini aman dirender iframe
        const renderable = checkIsRenderable(url, contentType);
        if (isMounted) setIsRenderable(renderable);

        // Langsung ambil ArrayBuffer karena PDF-lib butuh ArrayBuffer
        const arrayBuffer = await response.arrayBuffer();

        // ==========================================
        // 3. LOAD DENGAN PDF-LIB
        // ==========================================
        // (opsional: tambahkan logika modifikasi halaman di bawah baris ini jika diperlukan)
        const pdfDoc = await PDFDocument.load(arrayBuffer);

        // Simpan kembali PDF yang sudah di-load (berguna jika ada modifikasi)
        const newBytes = await pdfDoc.save();

        // ==========================================
        // 4. KONVERSI KE BLOB DAN URL
        // ==========================================
        // Buat blob dari tipe data uint8array kembalian pdfDoc.save()
        const finalBlob = new Blob([newBytes as unknown as ArrayBuffer], {
          type: contentType || "application/pdf",
        });

        const objectUrl = URL.createObjectURL(finalBlob);

        if (isMounted) {
          setBlobUrl(objectUrl);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Gagal memuat PDF dengan pdf-lib:", err);
        if (isMounted) {
          setError(true);
          setIsLoading(false);
        }
      }
    };

    loadAndProcessPdf();

    return () => {
      isMounted = false;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
        <p className="text-sm text-slate-500">Memuat pratinjau dokumen...</p>
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-900">
        <AlertCircle className="w-8 h-8 text-red-500 mb-3" />
        <p className="text-sm text-red-600 dark:text-red-400 mb-4">
          Gagal memuat pratinjau.
        </p>
        <Button asChild variant="outline" size="sm">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4 mr-2" /> Buka Langsung
          </a>
        </Button>
      </div>
    );
  }

  // JIKA FILE ADALAH PPT/DOCX/XLSX (Tidak bisa di-render Iframe)
  if (!isRenderable) {
    return (
      <div className="flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-800 p-6 text-center">
        <div className="p-3 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full mb-4">
          <FileDown className="w-8 h-8" />
        </div>
        <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">
          Format File Tidak Didukung Browser
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-sm">
          File ini tidak dapat dipratinjau secara langsung di dalam layar.
          Silakan unduh atau buka dokumen untuk melihat isinya.
        </p>
        <Button asChild>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4 mr-2" /> Buka atau Unduh Dokumen
          </a>
        </Button>
      </div>
    );
  }

  // JIKA BISA DI-RENDER (PDF/IMG/TXT)
  return (
    <div className="relative w-full h-[60vh] lg:h-[75vh] flex flex-col gap-2">
      <div className="flex justify-end lg:hidden">
        <Button
          asChild
          variant="secondary"
          size="sm"
          className="w-full text-xs"
        >
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Buka di Browser
          </a>
        </Button>
      </div>
      <iframe
        src={blobUrl}
        title="Pratinjau Berkas"
        className="w-full flex-1 rounded-lg border border-slate-200 dark:border-slate-800 bg-white"
      />
    </div>
  );
}
